import {
    registerBrick,
    Query,
    BrickContext,
    CloudObject,
    Brick,
    Order
} from 'olympe';
import { getLogger, ReactBrick, useProperty, useMUITheme } from '@olympeio/core'
import React, {useState} from 'react';
import { combineLatest, combineLatestWith } from 'rxjs';
import {switchMap} from 'rxjs/operators';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarExport, GridFooter, GridFooterContainer} from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { TextField } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {filterByValue, pSBC} from './helpers';
import { getIcon } from '@olympeio-extensions/commons';
import DatagridColumnConfig, {getValuesFromPaths} from "./DatagridColumnConfig";
import {getDataGridRenderer} from "./DataGridRegistry.jsx";
import Box from "@mui/material/Box";

export default class ODatagrid extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false),
        ]);
    }



    /**
     * @override
     */
    init($) {
        this.initConfig($);
        this.initData($);
    }



    /**
     * In charge of populating the context variable "Rows"
     * @private
     * @param {!BrickContext} $
     */
    initData($) {
        // Default value
        $.set('Rows', []);

        // Observe data
        $.observe('Data')
            .pipe(
                combineLatestWith([
                    $.observe('Columns', false),
                    $.observe('Refresh', false),
                ])
            )
            .subscribe(([data, columns]) => {
                let rows = [];

                if (data) {
                    rows = data.map((object, idx) => ({ id: idx, object }));
                }

                $.set('Rows', rows);

                if (columns.length === 0 && rows.length > 0) {
                    const elementsCache = $.get('Cell Elements');
                    elementsCache.clear();

                    const firstLine = rows[0].object;
                    let defaultColumns = [];
                    if (firstLine instanceof CloudObject) {
                        // defaultColumns = firstLine.getProperties().map((prop, idx) => {
                        //     return this.buildColumn(idx, prop.name(), true, 1, prop.name(), {}, 'left', {}, elementsCache, getDataGridRenderer('TEXT'), $);
                        // })
                    } else {
                        defaultColumns = Object.keys(firstLine).map((key, idx) => {
                            return this.buildColumn(idx, key, true, 1, key, {}, 'left', {}, elementsCache, getDataGridRenderer('TEXT'), $);
                        })
                    }
                    $.set('Columns', defaultColumns);
                }

            });
    }



    /**
     * In charge of populating the context variable "Columns"
     * @private
     * @param {!BrickContext} $
     */
    initConfig($) {

        // Default value
        $.set('Columns', []);
        $.set('Cell Elements', new Map()); // memoization

        // Observe config type
        $.observe('Config Type')
            .pipe(
                // Observe columns config
                switchMap((configType) =>
                    Query.instancesOf(configType, false)
                        .andReturn()
                        .sortBy(DatagridColumnConfig.rankProp, Order.ASC)
                        .observe($)
                ),
                // Observe each column properties
                switchMap((config) => combineLatest(config.map((column) => column.observeObject($)))),
                // Observe the layout
                combineLatestWith([
                    $.observe('Hide Toolbar'),
                ])
            )
            .subscribe(([columns]) => {
                // Reset elements
                const elementsCache = $.get('Cell Elements');
                elementsCache.clear();
                const masterColumns = [];

                // Compute final columns config
                for (const column of columns) {

                    // Compute column
                    const columnName = column[DatagridColumnConfig.headerProp.getTag()];
                    const columnOptions = column[DatagridColumnConfig.columnOptionsProp.getTag()];
                    const columnWidth = column[DatagridColumnConfig.columnWidthProp.getTag()];
                    const sortable = column[DatagridColumnConfig.sortableProp.getTag()];
                    const rendererType = column[DatagridColumnConfig.rendererTypeProp.getTag()];
                    const renderer = getDataGridRenderer(rendererType);
                    const rendererOptions = column[DatagridColumnConfig.rendererOptionsProp.getTag()];
                    const valuePaths = column[DatagridColumnConfig.propertyProp.getTag()];

                    let align = 'left';
                    if (['CURRENCY', 'NUMBER', 'PERCENT'].includes(rendererType)) {
                        align = 'right'
                    } else if (['BOOLEAN', 'ACTIONS'].includes(rendererType)) {
                        align = 'center';
                    }

                    const col = this.buildColumn(column.tag, columnName, sortable, columnWidth, valuePaths, rendererOptions, align, columnOptions, elementsCache, renderer, $);

                    masterColumns.push(col);
                }

                // Save config
                $.set('Columns', masterColumns);
            });
    }


    buildColumn(columnId, columnName, sortable, columnWidth, valuePaths, rendererOptions, align, columnOptions, elementsCache, renderer, $) {
        return {
            // Field
            field: columnId,
            headerName: columnName,

            // Config
            filterable: true,
            sortable: sortable ?? false,
            flex: columnWidth ?? 1,

            // Custom edition check
            align: align,
            headerAlign: align,

            // User Config
            ...columnOptions,

            // Header rendering
            renderHeader: () => (
                <Box
                    component={'span'}
                    className="MuiDataGrid-columnHeaderTitle"
                    sx={{fontWeight: 500, width: 1, textAlign: align}}
                >
                    {columnName?.toUpperCase()}
                </Box>
            ),

            // Rendering
            renderCell: (cell) => {
                const key = `${columnId}.${cell.row.id}`;
                let element = elementsCache.get(key);

                //We won't return Checkbox element from cache
                if (element === undefined) {
                    const values = getValuesFromPaths(cell.row.object, valuePaths);
                    element = renderer.render(values, rendererOptions, cell.row.object, $);
                    if ($.get('Enable Cache')) {
                        elementsCache.set(key, element);
                    }
                }
                return element;
            },

            sortComparator: (_v1, _v2, cellParams1, cellParams2) => {
                const value1 = cellParams1.value;
                const value2 = cellParams2.value;

                if (value1 > value2) {
                    return 1;
                } else if (value1 < value2) {
                    return -1;
                } else {
                    return 0;
                }
            },

            valueGetter: (cell) => {
                const values = getValuesFromPaths(cell.row.object, valuePaths);
                return values.length === 1
                    ? String(values[0])
                    : values.reduce((acc, cur) => `${acc}-${String(cur)}`, ''); // sinon on concat les valeurs dans une string
            }

        };
    }

    /**
     * @override
     */
    static getReactComponent($) {

        const onSelectedProps = $.get('On Row Selected');
        const onSelected = onSelectedProps instanceof Brick ? [$.runner(onSelectedProps), ...onSelectedProps.getInputs()] : null;

        return (props) => {
            const [hidden] = props.values;

            const columns = useProperty($, 'Columns', true) ?? [];
            const rows = useProperty($, 'Rows', true) ?? [];

            return !hidden && (
                <div style={{ height: '100%', width: '100%' }}>
                    <ODatagrid.MuiDataGrid
                        $={$}
                        rows={rows}
                        columns={columns}
                        onSelected={onSelected}
                    />
                </div>
            );
        }
    }
}

/**
 * @param {!BrickContext} $
 * @param {!Array<!Object>} rows
 * @param {!Array<!Object>} columns
 * @param {function()} onRefresh
 * @param {function()} onSelected
 */
ODatagrid.MuiDataGrid = ({ $, rows, columns, onSelected }) => {
    const [filteredRows, setFilteredRows] = useState(rows);
    const [pageSize, setPageSize] = useState();
    const height = useProperty($, 'Height');
    const rowHeight = useProperty($, 'Row Height') ?? 52;
    const rowCount = useProperty($, 'Row Count');


    React.useEffect(() => {
        setFilteredRows(rows);
    }, [rows]);

    /**
     * @param {string} value
     */
    const search = (value) => {
        if (value !== '') {
            setFilteredRows(filterByValue(rows, value));
        } else {
            setFilteredRows(rows);
        }
    };


    /**
     * Selection: conditionally fire event On Selected
     * @param {!Object|Object[]} newSelection
     */
    const onSelectionModelChange = (newSelection) => {
        // Get the index of the last selected row
        const lastIndex = newSelection[newSelection.length - 1];
        const [lastSelectedRow] = filteredRows.filter(row => row.id === lastIndex);

        if (lastSelectedRow !== undefined && onSelected) {
            const [onClick$, ctrlflowInput, rowInput] = onSelected;
            onClick$
                .set(rowInput, lastSelectedRow.object)
                .trigger(ctrlflowInput);
        }
    };

    /**
     * Selection: conditionally fire event On Selected
     * @param {number} newPage
     * @param {number} _pageSize
     */
    const onPageChange = (newPage, _pageSize) => {
        let realPageSize = pageSize;
        if(_pageSize !== undefined) {
            realPageSize = _pageSize;
        }

        // If we define a row count from the outside (pagination handled by server)
        // And we're changing page to a page that doesn't have all the data in local
        // We trigger the event to allow the user to fetch more data
        // const rowCount = $.get('Row Count');
        if (rowCount) {
            if(rows.length !== rowCount) {
                if ((newPage+1) * realPageSize > rows.length) {
                    const onNewPage = $.get('On New Page');
                    if (onNewPage) {
                        const onNewPage$ = $.runner(onNewPage);
                        const [ctrlFlowInput, newPageInput, pageSizeInput] = onNewPage.getInputs();
                        onNewPage$
                            .set(newPageInput, newPage)
                            .set(pageSizeInput, realPageSize)
                            .trigger(ctrlFlowInput);
                    } else {
                        getLogger('DataGrid').warn('Should fetch a new page, but no handler is defined on the "On New Page" Event')
                    }
                }
            }
        }

    };

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
            </GridToolbarContainer>
        );
    };

    const CustomFooter = () => {
        return (
            <GridFooterContainer>
                <IconButton
                    aria-label="refresh"
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ paddingLeft: 10 }}
                    onClick={() => $.trigger('Refresh')}
                >{getIcon('refresh')}</IconButton>
                <GridFooter />
            </GridFooterContainer>
        );
    };


    const hideToolbar = useProperty($,'Hide Toolbar')
    const myComponents = {Footer: CustomFooter}
    if(!hideToolbar) {
        myComponents.Toolbar = CustomToolbar;
    }


    const theme = useMUITheme($);
    return (
        <ThemeProvider theme={theme}>
            <div style={{position: 'absolute', top: '0px', bottom: '0px', width: '100%'}}>
                <DataGrid
                    rows={filteredRows}
                    rowCount={rowCount ?? filteredRows.length}
                    columns={columns}
                    rowHeight={rowHeight}
                    autoPageSize={true}
                    components={myComponents}
                    onSelectionModelChange={onSelectionModelChange}
                    onPageSizeChange={(s) => {setPageSize(s); onPageChange(0, s)}}
                    onPageChange={(newPage) => onPageChange(newPage)}
                    loading={useProperty($,'Show Loader') ?? false}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
                    }
                    sx={{
                        '&, [class^=MuiDataGrid]': {
                            border: 'none'
                        },
                        '& .MuiDataGrid-main': {
                            border: '1px solid '+ theme.palette.secondary.main, borderRadius: '5px',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.getContrastText(theme.palette.secondary.main),
                            minHeight: '40px !important',
                            lineHeight: '40px !important',
                            maxHeight: '40px !important'
                        },
                        '& .MuiDataGrid-row.Mui-even': {
                            backgroundColor: pSBC(0.7, theme.palette.secondary.main),
                            borderTop: '1px solid ' + theme.palette.secondary.main,
                            borderBottom: '1px solid ' + theme.palette.secondary.main,
                            boxSizing: 'border-box'
                        },
                        '& .MuiDataGrid-row.Mui-odd': {
                            backgroundColor: 'white',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            marginTop: '40px !important',
                            height: (height - 52 - 40) + 'px !important' // 52: footer, 40: header
                        },
                        '& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)': {
                            display: 'none !important'
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.getContrastText(theme.palette.secondary.main),
                        },
                        '& .MuiDataGrid-row.Mui-selected:hover': {
                            backgroundColor: pSBC(-0.1, theme.palette.secondary.main)
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.getContrastText(theme.palette.secondary.light),
                        },
                        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                            outline: 'none'
                        }
                    }}
                />
            </div>
            {!hideToolbar && <ODatagrid.Search search={search} />}
        </ThemeProvider>
    );

};

/**
 * @param {boolean} isShown
 * @param {function(string)} search
 */
ODatagrid.Search = ({ search }) => {
    return (
        <TextField
            id="outlined-basic"
            variant="standard"
            placeholder="search..."
            size="small"
            style={{
                height: '30px', width: '175px',
                position: 'absolute', right: '5px', top: '5px',
                display: 'block',
            }}
            onChange={(event) => search(event.target.value)}
        />
    );
};


registerBrick('019271ec76de464b0667', ODatagrid);
